import { Location } from '@angular/common';
import { Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Platform } from '@app/classes';
import { FeatureFlag } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { TimesheetEntry } from '@app/models/time-tracking/timesheet-entry.model';
import { TimeTrackingSettingsService } from '@app/modules/time-tracking/services/time-tracking-settings.service';
import { AppDatePipe } from '@app/pipes';
import { FeatureService } from '@app/services/feature.service';
import moment from 'moment';
import { Settings } from '../../interfaces/settings.interface';
import { EnteringFor } from '../../interfaces/timesheet-entering-for.type';
import { TimesheetEntryService } from '../../services';
import { EnterHoursComponentCard } from '../enter-hours-card/enter-hours-card.component';

type employeeId = number;

/**
 * dateString is a string in format YYYY-MM-DD
 */
type dateString = string;

type minutesMap = Map<employeeId, employeeMinutesMap>;

type employeeMinutesMap = Map<dateString, number>;

/**
 * dateRegex - YYYY-MM-DD
 */
const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

const datePipe = new AppDatePipe();

const MINUTES_IN_DAY = 1440;

@Component({
    selector: 'app-enter-hours-list',
    templateUrl: './enter-hours-list.template.html',
})
export class EnterHoursList {
    @ViewChildren('entryCards') entryCards!: QueryList<EnterHoursComponentCard>;

    @Input() timesheetEntries = [new TimesheetEntry()];
    /**
     * employees is a list of employees that the user can select from
     *
     * These employees _must_ have their project assignments loaded
     * so that we can display the projects that the user can select from
     */
    @Input() employees: Employee[] = [];

    /**
     * enteringFor is a string that determines whether or not to display the employee select
     * 'self' - do not display the employee select
     * 'other' - display the employee select but do not allow the user to change the employee
     * 'many' - display the employee select and allow the user to change the employee
     */
    @Input() enteringFor: EnteringFor = 'self';

    @Input() projectCanBeChanged = true;

    // These overlap errors are from the server. The differ from overlapFieldErrors
    // in that they are not generated from the user's actions, but from the server.
    @Input() overlapErrors: string[] = [];

    @Output() onSave = new EventEmitter<void>();

    /**
     * editMode is true if the user is editing an existing timesheet entry
     * in this case, we will only show one timesheet entry card
     */
    editMode = false;

    bannerErrorString = '';
    showErrorBanner = false;

    valid = false;

    defaultApprovalFlow: 'manager' | 'automatic' = 'manager';

    companyIsOnPayrollIntegration = false;

    settings: Settings = {};

    /**
     * maxHoursWarnings are the banners at the top of the component
     * that show which employees have gone over the maximum of 24 hours in one day
     */
    maxHoursWarnings: {
        employeeName: string;
        localeDate: string;
    }[] = [];

    /**
     * editBanners control if banners on the edit screen
     * can be seen
     */
    editBanners = {
        employeeRemovedFromTimeTracking: false,
        employeeRemovedFromProject: false,
        projectArchived: false,
    };

    hasTimeClock = false;

    /**
     * Stores information about overlapping time entries
     * Key is the index of the entry, value is an array of indices of overlapping entries
     */
    overlapFieldErrors: { [key: number]: { start: number[]; end: number[] } } = {};

    /**
     * totalMinutesMap stores the hours already recorded for an employee
     * on a particular day. It is only the data already persisted, not the newly entered data.
     */
    private totalMinutesMap: minutesMap = new Map();

    constructor(
        private location: Location,
        private timesheetEntryService: TimesheetEntryService,
        private timeTrackingSettingsService: TimeTrackingSettingsService,
        private featureService: FeatureService
    ) {}

    async ngOnInit(): Promise<void> {
        this.hasTimeClock = await this.featureService.has(FeatureFlag.timeClock);
    }

    async ngOnChanges(): Promise<void> {
        this.editMode = this.timesheetEntries.some((entry) => entry.isPersisted);

        if (this.editMode) {
            this.updateEditBanners();
        }

        this.updateMinutesMap(this.timesheetEntries[0]);
        // This is cached so calling it many times is fine
        await this.initSettings();
        this.revalidate();
    }

    onAddTimeEntry(): void {
        if (this.editMode) {
            return;
        }

        const lastTimesheetEntry = this.timesheetEntries.slice(-1)[0];
        const newTimesheetEntry = new TimesheetEntry();

        // using hasOne setters to set relationship
        newTimesheetEntry.workedOn = lastTimesheetEntry.workedOn;
        newTimesheetEntry.employee = lastTimesheetEntry.employee;
        newTimesheetEntry.project = lastTimesheetEntry.project;

        newTimesheetEntry.incomeType = lastTimesheetEntry.incomeType;
        newTimesheetEntry.incomeTypeId = lastTimesheetEntry.incomeTypeId;
        newTimesheetEntry.salaryId = lastTimesheetEntry.salaryId;

        this.timesheetEntries.push(newTimesheetEntry);

        // a new entry will make this form invalid until hours are entered
        this.revalidate();
    }

    removeEntry(index: number): void {
        if (this.editMode) {
            return;
        }

        this.timesheetEntries.splice(index, 1);
        this.updateWarnings();
        this.checkForOverlappingStartAndEndTimesInTimesheetEntries();
        this.revalidate();
    }

    onBack(): void {
        this.location.back();
    }

    async onChangeEmployeeOrWorkedOn(timesheetEntry: TimesheetEntry): Promise<void> {
        await this.updateMinutesMap(timesheetEntry);
        this.updateWarnings();
        this.checkForOverlappingStartAndEndTimesInTimesheetEntries();
        this.revalidate();
    }

    async onChangeMinutes(): Promise<void> {
        this.updateWarnings();
        this.checkForOverlappingStartAndEndTimesInTimesheetEntries();
        this.revalidate();
    }

    async onClickSave(): Promise<void> {
        let allFormsValid = true;

        this.entryCards.forEach((entryCard: EnterHoursComponentCard) => {
            const form = entryCard.form;
            if (!this.submitAndValidate(form)) {
                allFormsValid = false;
            }
        });

        if (!allFormsValid) {
            return;
        }

        this.onSave.emit();
    }

    private submitAndValidate(ngForm: NgForm): boolean | null {
        ngForm.onSubmit(new SubmitEvent('submit'));

        return ngForm.valid;
    }

    /**
     * fetchMinutes returns the number of minutes worked on a day for an employee
     */
    private async fetchMinutes(employee: Employee, date: dateString): Promise<number> {
        if (!dateRegex.test(date)) {
            return 0;
        }

        const minutesWorkedOnDay = await this.timesheetEntryService.minutesWorkedOnDay(employee.id, date);

        // handles the case where the minutes returned from the backend
        // include the timesheet we are editing
        // this is run on component load and cached
        if (this.editMode && this.timesheetEntries[0].workedOn === date) {
            return minutesWorkedOnDay - this.timesheetEntries[0].minutes;
        }

        return minutesWorkedOnDay;
    }

    /**
     * updateMinutesMap makes sure the minutesMap is filled with the correct data
     */
    private async updateMinutesMap(timesheetEntry: TimesheetEntry): Promise<void> {
        if (!timesheetEntry.employeeId || !timesheetEntry.workedOn) {
            return;
        }

        if (!this.totalMinutesMap.has(timesheetEntry.employeeId)) {
            this.totalMinutesMap.set(timesheetEntry.employeeId, new Map());
        }

        const employeeMap = this.totalMinutesMap.get(timesheetEntry.employeeId) as employeeMinutesMap;

        // if the minutes for this day are already loaded for this employee, nothing more to do
        // we only hit the back end once per date per employee
        if (employeeMap.has(timesheetEntry.workedOn)) {
            return;
        }

        const minutesWorkedOnDate = await this.fetchMinutes(timesheetEntry.employee, timesheetEntry.workedOn);

        employeeMap.set(timesheetEntry.workedOn, minutesWorkedOnDate);
    }

    private updateWarnings(): void {
        const currentMinutesMap = this.createCurrentMinutesMap();
        const warnings: { empId: employeeId; date: dateString }[] = [];

        currentMinutesMap.forEach((employeeMap: employeeMinutesMap, empId: employeeId) => {
            employeeMap.forEach((minutes: number, date: dateString) => {
                const persistedMinutesForDay = this.totalMinutesMap.get(empId)?.get(date) ?? 0;

                if (minutes + persistedMinutesForDay > MINUTES_IN_DAY) {
                    warnings.push({ empId, date });
                }
            });
        });

        this.maxHoursWarnings = warnings.map((warning) => {
            const localeDate = datePipe.transform(moment(warning.date), 'longDate') ?? '';
            const employeeName = this.employees.find((e) => e.id === warning.empId)?.fullName ?? '';

            return { employeeName, localeDate };
        });
    }

    private createCurrentMinutesMap(): minutesMap {
        const currentMinutesMap: minutesMap = new Map();

        this.timesheetEntries.forEach((timesheetEntry) => {
            const entryCanBeAddedToMap = timesheetEntry.minutes && timesheetEntry.employeeId && timesheetEntry.workedOn;
            if (!entryCanBeAddedToMap) {
                return;
            }

            if (!currentMinutesMap.has(timesheetEntry.employeeId)) {
                currentMinutesMap.set(timesheetEntry.employeeId, new Map());
            }

            const employeeMap = currentMinutesMap.get(timesheetEntry.employeeId) as employeeMinutesMap;
            const minutesToday = employeeMap.get(timesheetEntry.workedOn) ?? 0;

            employeeMap.set(timesheetEntry.workedOn, minutesToday + timesheetEntry.minutes);
        });

        return currentMinutesMap;
    }

    private revalidate(): void {
        if (this.maxHoursWarnings.length > 0 || Object.keys(this.overlapFieldErrors).length > 0) {
            this.valid = false;
            return;
        }

        for (const timesheetEntry of this.timesheetEntries) {
            if (!timesheetEntry.minutes || !timesheetEntry.workedOn || !timesheetEntry.employeeId) {
                this.valid = false;
                return;
            }

            if (
                this.hasTimeClock &&
                timesheetEntry.timeTrackingMethod === 'start_end_time' &&
                (!timesheetEntry.startTime || !timesheetEntry.endTime)
            ) {
                this.valid = false;
                return;
            }

            if (this.hasTimeClock && timesheetEntry.timeTrackingMethod !== this.settings.timeTrackingMethod) {
                this.valid = false;
                return;
            }
        }

        this.valid = true;
    }

    private async initSettings(): Promise<void> {
        this.settings = await this.timeTrackingSettingsService.get();
        this.defaultApprovalFlow = this.settings.noProjectApprovalFlow ?? 'manager';
        this.companyIsOnPayrollIntegration = this.settings.payrollIntegration ?? false;
    }

    /**
     * updateEditBanners decides if certain banners that
     * only appear on the edit screen should be visible.
     */
    private updateEditBanners(): void {
        const timesheetEntry = this.timesheetEntries[0];
        const employee = timesheetEntry.employee;

        if (!timesheetEntry.employee.hasModule(Platform.modules.timeTracking)) {
            this.editBanners.employeeRemovedFromTimeTracking = true;
            return;
        }

        if (timesheetEntry.project?.isArchived) {
            this.editBanners.projectArchived = true;
            return;
        }

        if (timesheetEntry.project) {
            const project = timesheetEntry.project;
            const userStillOnProject = employee.timeTrackingProjectAssignmentsUserCanEnterHoursFor.some((pa) =>
                pa.project.is(project)
            );
            this.editBanners.employeeRemovedFromProject = !userStillOnProject;
        }
    }

    /**
     * Checks for overlapping time entries within the timesheetEntries array.
     * This function compares each entry with every other entry for the same employee,
     * identifying any overlaps in start and end times. It populates the overlapErrors
     * object with information about any detected overlaps, which can be used for
     * validation and error display purposes.
     */
    private checkForOverlappingStartAndEndTimesInTimesheetEntries(): void {
        this.overlapFieldErrors = {};

        this.timesheetEntries.forEach((entry, index) => {
            if (!entry.startTime || !entry.employeeId || !entry.workedOn) return;

            const currentStart = moment(entry.startTime);
            // We add 1 minute to the start time if there's no end time to create a minimal
            // duration for entries without an end time. This ensures that such entries are
            // still considered when checking for overlaps, treating them as if they lasted
            // for at least one minute. Without this, entries with only a start time might
            // be incorrectly seen as non-overlapping with other entries.
            const currentEnd = entry.endTime ? moment(entry.endTime) : currentStart.clone().add(1, 'minute');

            this.timesheetEntries.forEach((otherEntry, otherIndex) => {
                const isSameEntry = index === otherIndex;
                const isValidOtherEntry = otherEntry.startTime && otherEntry.employeeId && otherEntry.workedOn;
                const isSameEmployee = entry.employeeId === otherEntry.employeeId;
                const isSameWorkedOn = entry.workedOn === otherEntry.workedOn;

                if (isSameEntry || !isValidOtherEntry || !isSameEmployee || !isSameWorkedOn) {
                    return;
                }

                const otherStart = moment(otherEntry.startTime);
                const otherEnd = otherEntry.endTime ? moment(otherEntry.endTime) : otherStart.clone().add(1, 'minute');

                const overlaps = currentStart.isBefore(otherEnd) && currentEnd.isAfter(otherStart);

                if (overlaps) {
                    if (!this.overlapFieldErrors[index]) {
                        this.overlapFieldErrors[index] = { start: [], end: [] };
                    }
                    this.overlapFieldErrors[index].start.push(otherIndex);
                    this.overlapFieldErrors[index].end.push(otherIndex);
                }
            });
        });
    }
}
